import { StationeryTemplateCategoryEnum } from '@graphql/generated-base';
import type { GalleryFacet, GalleryFormatParam } from './buildGalleryFilterHref';

/**
 * Single source of truth for the expanded (mega-menu) stationery gallery nav.
 *
 * Every link is DATA here; hrefs are resolved at render time by `useResolvedMegaNav`
 * (which knows whether we are in the public shop or embedded in an event's admin).
 * Labels are keys resolved by `useMegaNavLabels` so `tq` calls stay lexically inside a hook.
 *
 * URL contract (verified against the gallery filter code):
 * - `format` only steers tile price display; it never filters (`useFormatSearchParam`).
 * - Filtering is `pf=<facet>`; the gallery ANDs the selected facet groups (OR within a group), so a
 *   deep link carries at most ONE facet group (`GalleryFacet` enforces this).
 */

const { saveTheDate, invitation, thankYou, holiday, enclosure, sign, menu, napkin } = StationeryTemplateCategoryEnum;

export type MegaNavSlotId = 'saveTheDate' | 'invitation' | 'ceremonyReception' | 'thankYou' | 'holiday';

export type MegaNavTextKey =
  | 'ceremonyReception'
  | 'allSaveTheDates'
  | 'allInvitations'
  | 'allThankYouCards'
  | 'allHolidayCards'
  | 'shopByFormat'
  | 'printed'
  | 'digital'
  | 'freeDigital'
  | 'shopByFeature'
  | 'foil'
  | 'magnet'
  | 'postcard'
  | 'photo'
  | 'shopByStyle'
  | 'botanical'
  | 'floral'
  | 'elegantClassic'
  | 'destination'
  | 'typographic'
  | 'rustic'
  | 'minimal'
  | 'vintage'
  | 'whimsical'
  | 'suiteInserts'
  | 'enclosureCards'
  | 'signs'
  | 'menus'
  | 'napkins'
  | 'servicesTools'
  | 'envelopes'
  | 'freeSamplePack'
  | 'designConcierge'
  | 'wordingEtiquette'
  | 'collectAddresses';

/** Either a fixed string key or a stationery category (resolved via `dashboardCategoryNav` translations). */
export type MegaNavLabel = MegaNavTextKey | Readonly<{ category: StationeryTemplateCategoryEnum }>;

export type MegaNavTarget =
  | Readonly<{ kind: 'gallery'; category: StationeryTemplateCategoryEnum; facet?: GalleryFacet; format?: GalleryFormatParam }>
  /** Admin-only route: `getSamplePackPath()` when embedded in an event, else the login-gated public deep link. */
  | Readonly<{ kind: 'samplePack' }>
  /**
   * `getEnvelopesPath()`, which resolves to the event's own route when embedded and the public
   * `/cards/envelopes` mount otherwise — that mount handles its own sign-in, so unlike the sample
   * pack there is no login redirect to route a logged-out visitor through.
   */
  | Readonly<{ kind: 'envelopes' }>
  /**
   * An admin route outside the cards app, by its path under `/<eventHandle>/edit/`. Embedded in an
   * event it is the event's own route. Public visitors get `publicHref` when given, else the
   * `/event/edit/…` redirect, which logs them in (or signs them up) and picks their event.
   */
  | Readonly<{ kind: 'adminRoute'; path: string; publicHref?: string }>
  /** Non-SPA destination (marcom / help / blog); rendered as a plain anchor. */
  | Readonly<{ kind: 'href'; href: string }>;

export type MegaNavLink = Readonly<{ id: string; label: MegaNavLabel; target: MegaNavTarget }>;

export type MegaNavColumn = Readonly<{
  id: string;
  /** Column headings are labels ("Shop by …"), not links; every destination is a row. */
  heading: MegaNavLabel;
  links: ReadonlyArray<MegaNavLink>;
}>;

export type MegaNavTile = Readonly<{ id: string; label: MegaNavLabel; imageSrc: string; target: MegaNavTarget }>;

export type MegaNavSlot = Readonly<{
  id: MegaNavSlotId;
  label: MegaNavLabel;
  /** Omitted for grouped slots (Ceremony & Reception) that have no gallery of their own. */
  target?: Extract<MegaNavTarget, { kind: 'gallery' }>;
  /**
   * Fully translated "All <category>" label for surfaces where the slot itself can't
   * navigate (the mobile sheet). A whole phrase per category, never "All" + noun, so
   * translators can inflect it.
   */
  allLabel?: MegaNavTextKey;
  activeForCategories: ReadonlyArray<StationeryTemplateCategoryEnum>;
  columns: ReadonlyArray<MegaNavColumn>;
  tiles?: ReadonlyArray<MegaNavTile>;
}>;

const gallery = (category: StationeryTemplateCategoryEnum, facet?: GalleryFacet, format?: GalleryFormatParam): MegaNavTarget => ({
  kind: 'gallery',
  category,
  facet,
  format,
});

/**
 * Format = how the design is delivered. `format:*` facets filter; `format=` steers the
 * tile price display to match. "Free Digital" is the gallery's `price:basic` tag.
 * No "Premium" row: everything that isn't free digital is premium, so it was redundant.
 */
const formatColumn = (category: StationeryTemplateCategoryEnum, options: Readonly<{ freeDigital: boolean }>): MegaNavColumn => ({
  id: 'format',
  heading: 'shopByFormat',
  links: [
    { id: 'printed', label: 'printed', target: gallery(category, 'format:paper', 'paper') },
    { id: 'digital', label: 'digital', target: gallery(category, 'format:digital', 'premiumDigital') },
    ...(options.freeDigital ? [{ id: 'free-digital', label: 'freeDigital' as const, target: gallery(category, 'price:basic', 'premiumDigital') }] : []),
  ],
});

/**
 * Feature = a positive attribute of the design (Jeff's ask: feature-based rather than
 * photo / non-photo). Magnet and postcard exist only on the postcard categories.
 */
const featureColumn = (category: StationeryTemplateCategoryEnum, options: Readonly<{ foil: boolean; magnet: boolean; postcard: boolean }>): MegaNavColumn => ({
  id: 'feature',
  heading: 'shopByFeature',
  links: [
    // Holiday cards are never foiled (Marinda, 2026-10-22), so the row would filter to nothing.
    ...(options.foil ? [{ id: 'foil', label: 'foil' as const, target: gallery(category, 'foil:with') }] : []),
    ...(options.magnet ? [{ id: 'magnet', label: 'magnet' as const, target: gallery(category, 'product:magnet', 'paper') }] : []),
    // `size:postcard` is only tagged on the postcard categories (save-the-date, thank-you).
    ...(options.postcard ? [{ id: 'postcard', label: 'postcard' as const, target: gallery(category, 'size:postcard', 'paper') }] : []),
    { id: 'photo', label: 'photo', target: gallery(category, 'photo:with') },
  ],
});

const STYLE_LINKS: ReadonlyArray<Readonly<{ id: string; label: MegaNavTextKey; facet: GalleryFacet }>> = [
  { id: 'botanical', label: 'botanical', facet: 'style:botanical' },
  { id: 'floral', label: 'floral', facet: 'style:floral' },
  { id: 'elegant-classic', label: 'elegantClassic', facet: 'style:elegant|classic' },
  { id: 'destination', label: 'destination', facet: 'style:destination' },
  { id: 'typographic', label: 'typographic', facet: 'style:typographic' },
  { id: 'rustic', label: 'rustic', facet: 'style:rustic' },
  { id: 'minimal', label: 'minimal', facet: 'style:minimal' },
  { id: 'vintage', label: 'vintage', facet: 'style:vintage' },
  { id: 'whimsical', label: 'whimsical', facet: 'style:whimsical' },
];

/** Style facets exist only where the StyleShowcase does today: save-the-date and invitation. */
const styleColumn = (category: StationeryTemplateCategoryEnum): MegaNavColumn => ({
  id: 'style',
  heading: 'shopByStyle',
  links: STYLE_LINKS.map(style => ({ id: style.id, label: style.label, target: gallery(category, style.facet) })),
});

/** Identical in every dropdown. The envelopes row is dropped by `useResolvedMegaNav` while `printEnvelopeProduct` is off. */
export const SERVICES_COLUMN: MegaNavColumn = {
  id: 'services',
  heading: 'servicesTools',
  links: [
    { id: 'envelopes', label: 'envelopes', target: { kind: 'envelopes' } },
    { id: 'sample-pack', label: 'freeSamplePack', target: { kind: 'samplePack' } },
    { id: 'concierge', label: 'designConcierge', target: { kind: 'href', href: '/help/en/articles/9765459-premium-invitation-suites' } },
    { id: 'wording', label: 'wordingEtiquette', target: { kind: 'href', href: '/blog/wedding-invitation-wording' } },
    // Embedded in an event: the event's contact collector. Public gallery: the how-to article
    // (Amy, 2026-10-07) — a logged-out visitor has no event to collect addresses into.
    {
      id: 'contacts',
      label: 'collectAddresses',
      target: { kind: 'adminRoute', path: 'collect-contacts', publicHref: '/blog/how-to-easily-collect-addresses-for-wedding-invitations/' },
    },
  ],
};

/**
 * Per-slot tile imagery (Marinda, 2026-10-22). One shot per destination rather than one per
 * category, so a tile shows the thing it links to. Published to `joy-public-assets-bucket` under
 * `print/category-nav/`, the folder the Ceremony tiles already came from — it also holds the five
 * per-category shots the cart's "Explore also" strip and the Cards rail use, which predate this
 * convention and are not checked in. `src/assets/print/mega-nav/` is provenance for these
 * seventeen only, the same arrangement as the browser icon set
 * (`config/webpack/iconAssets.spec.ts`).
 */
const MEGA_NAV_TILE_IMAGE_BASE = 'https://withjoy.com/assets/public/print/category-nav';
const tile = (id: string, label: MegaNavTextKey, image: string, target: MegaNavTarget): MegaNavTile => ({
  id,
  label,
  imageSrc: `${MEGA_NAV_TILE_IMAGE_BASE}/${image}.png`,
  target,
});

export const MEGA_NAV_SLOTS: ReadonlyArray<MegaNavSlot> = [
  {
    id: 'saveTheDate',
    label: { category: saveTheDate },
    target: { kind: 'gallery', category: saveTheDate },
    allLabel: 'allSaveTheDates',
    activeForCategories: [saveTheDate],
    tiles: [
      tile('foil', 'foil', 'savethedate_foil', gallery(saveTheDate, 'foil:with')),
      tile('photo', 'photo', 'savethedate_photo', gallery(saveTheDate, 'photo:with')),
      tile('magnet', 'magnet', 'savethedate_magnet', gallery(saveTheDate, 'product:magnet', 'paper')),
      tile('postcard', 'postcard', 'savethedate_postcard', gallery(saveTheDate, 'size:postcard', 'paper')),
    ],
    columns: [
      formatColumn(saveTheDate, { freeDigital: true }),
      featureColumn(saveTheDate, { foil: true, magnet: true, postcard: true }),
      styleColumn(saveTheDate),
      SERVICES_COLUMN,
    ],
  },
  {
    id: 'invitation',
    label: { category: invitation },
    target: { kind: 'gallery', category: invitation },
    allLabel: 'allInvitations',
    // Enclosure cards live under Invitations, as a tile rather than a one-row column.
    activeForCategories: [invitation, enclosure],
    tiles: [
      tile('foil', 'foil', 'inv_foil', gallery(invitation, 'foil:with')),
      tile('photo', 'photo', 'inv_photo', gallery(invitation, 'photo:with')),
      tile('enclosure', 'enclosureCards', 'inv_enclosure', gallery(enclosure)),
      tile('sample-pack', 'freeSamplePack', 'inv_sample', { kind: 'samplePack' }),
    ],
    columns: [formatColumn(invitation, { freeDigital: true }), featureColumn(invitation, { foil: true, magnet: false, postcard: false }), styleColumn(invitation), SERVICES_COLUMN],
  },
  {
    id: 'ceremonyReception',
    label: 'ceremonyReception',
    activeForCategories: [sign, menu, napkin],
    // The tiles ARE the category links; a text column repeating them was redundant.
    tiles: [
      tile('signs', 'signs', 'ceremony_sign', gallery(sign)),
      tile('menus', 'menus', 'ceremony_menu', gallery(menu)),
      tile('napkins', 'napkins', 'ceremony_napkins', gallery(napkin)),
    ],
    columns: [SERVICES_COLUMN],
  },
  {
    id: 'thankYou',
    label: { category: thankYou },
    target: { kind: 'gallery', category: thankYou },
    allLabel: 'allThankYouCards',
    activeForCategories: [thankYou],
    tiles: [
      tile('foil', 'foil', 'thankyou_foil', gallery(thankYou, 'foil:with')),
      tile('photo', 'photo', 'thankyou_photo', gallery(thankYou, 'photo:with')),
      tile('magnet', 'magnet', 'thankyou_magnet', gallery(thankYou, 'product:magnet', 'paper')),
      tile('postcard', 'postcard', 'thankyou_postcard', gallery(thankYou, 'size:postcard', 'paper')),
    ],
    // No Free Digital row: `useDesignTemplates` skips the digital designs outside save-the-date
    // and invitation, so no thank-you template is ever tagged `price:basic` and the link filtered
    // to nothing (Jeff, 2026-10-29).
    columns: [formatColumn(thankYou, { freeDigital: false }), featureColumn(thankYou, { foil: true, magnet: true, postcard: true }), SERVICES_COLUMN],
  },
  {
    id: 'holiday',
    label: { category: holiday },
    target: { kind: 'gallery', category: holiday },
    allLabel: 'allHolidayCards',
    activeForCategories: [holiday],
    // Two tiles, not four: holiday has no foil and no magnet or postcard sizes, and the sample
    // pack is wedding stationery only (Amy, 2026-10-22).
    tiles: [tile('envelopes', 'envelopes', 'holiday_envelopes', { kind: 'envelopes' }), tile('photo', 'photo', 'holiday_photo', gallery(holiday, 'photo:with'))],
    // No free tier in this category.
    columns: [formatColumn(holiday, { freeDigital: false }), featureColumn(holiday, { foil: false, magnet: false, postcard: false }), SERVICES_COLUMN],
  },
];
